import * as amplitude from "@amplitude/analytics-browser";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useHtmlEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useRefMerged } from "@redotech/react-util/ref";
import { useTranslation } from "@redotech/redo-i18n";
import LayoutLeft from "@redotech/redo-icons/layout-left.svg";
import LayoutLeftFilled from "@redotech/redo-icons/layout-left_filled.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import GearIcon from "@redotech/redo-icons/settings-02.svg";
import GearFilledIcon from "@redotech/redo-icons/settings-02_filled.svg";
import classNames from "classnames";
import {
  createContext,
  JSXElementConstructor,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import * as darkModeCss from "../../../theme/component-colors-dark.module.css";
import { RedoIcon } from "../../icon/redo-icon";
import type { RedoListItem } from "../../list/redo-list";
import { RedoListDropdown } from "../../list/redo-list-dropdown";
import { Tooltip } from "../../tooltip/tooltip";
import * as navigationCss from "../redo-navigation.module.css";
import {
  PrimaryNavOverflowPopover,
  tabToListItem,
  usePrimaryNavOverflow,
} from "./redo-primary-nav-rail";
import { PrimaryNavigationTab } from "./redo-primary-nav-tab";

interface BasePrimaryTab {
  "data-dd-action-name"?: string;
  id: string;
  hasNotification: boolean;
  UnfilledIcon: JSXElementConstructor<any>;
  FilledIcon: JSXElementConstructor<any>;
  friendlyName: string;
  hideLabel?: boolean;
}

export interface LinkPrimaryTab extends BasePrimaryTab {
  type: "link";
  href: string;
  pathRoot: string;
}

interface ButtonPrimaryTab extends BasePrimaryTab {
  type: "button";
  onClick: () => void;
}

export type PrimaryTab = LinkPrimaryTab | ButtonPrimaryTab;

interface PrimaryTabSections {
  core: LinkPrimaryTab[];
  activeProducts: LinkPrimaryTab[];
  inactiveProducts: LinkPrimaryTab[];
}

interface RedoPrimaryNavProps {
  toggleCollapseMode: () => void;
  isCollapseMode: boolean;
  enterSettingsUrl: string | null;
  settingsRoot: string;
  primaryTabSections: PrimaryTabSections;
  selectedTabId: string | undefined;
  teamSwitcher?: ReactNode;
  actions?: ReactNode;
  userProfile?: ReactNode;
}

/**
 * Applies a stored order to a list of tabs. Tabs present in storedOrder
 * come first (in that order), followed by any new tabs not in the stored order.
 */
function applyStoredOrder(
  tabs: LinkPrimaryTab[],
  storedOrder: string[],
): LinkPrimaryTab[] {
  if (storedOrder.length === 0) {
    return tabs;
  }
  const tabMap = new Map(tabs.map((tab) => [tab.id, tab]));
  const ordered: LinkPrimaryTab[] = [];
  for (const id of storedOrder) {
    const tab = tabMap.get(id);
    if (tab) {
      ordered.push(tab);
      tabMap.delete(id);
    }
  }
  // Append any tabs not in stored order (new products)
  for (const tab of tabMap.values()) {
    ordered.push(tab);
  }
  return ordered;
}

const ADD_SLOT_ID = "primary-nav-add";

const PrimaryNavAddPopover = memo(function PrimaryNavAddPopover({
  availableTabs,
  selectedTabId,
  measureRef,
}: {
  availableTabs: LinkPrimaryTab[];
  selectedTabId: string | undefined;
  measureRef: (element: HTMLElement | null) => void;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const iconOnly = useContext(PrimaryNavIconOnlyContext);
  const navigate = useNavigate();

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const listItems: RedoListItem<LinkPrimaryTab>[] = useMemo(
    () => availableTabs.map(tabToListItem),
    [availableTabs],
  );

  const handleItemSelected = useCallback(
    ({ item }: { item: RedoListItem<LinkPrimaryTab> }) => {
      const tab = item.value;
      amplitude.track("nav/sidebar-item-clicked", {
        section: tab.id,
        item: tab.id,
      });
      navigate(tab.href);
    },
    [navigate],
  );

  const buttonRef = useRefMerged<HTMLElement>([setAnchorEl, measureRef]);

  const addButton = (
    <Flex
      align="center"
      as="button"
      className={navigationCss.primaryTab}
      data-testid="primary-nav-add"
      dir="column"
      gap="xxs"
      justify="center"
      onClick={handleOpen}
      ref={buttonRef}
    >
      <Flex
        align="center"
        className={navigationCss.primaryTabIconBox}
        justify="center"
      >
        <RedoIcon color="tertiary" Icon={PlusIcon} size={20} />
      </Flex>
      {!iconOnly && (
        <Text
          as="span"
          fontSize="xxs"
          textColor="secondary"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {t("Add", { context: "navigation menu" })}
        </Text>
      )}
    </Flex>
  );

  return (
    <>
      {iconOnly ? (
        <Tooltip
          darkMode
          placement="right"
          title={t("Add", { context: "navigation menu" })}
        >
          {addButton}
        </Tooltip>
      ) : (
        addButton
      )}
      <RedoListDropdown
        darkMode
        dropdownAnchor={anchorEl}
        dropdownOpen={open}
        dropdownPlacement="right-start"
        items={listItems}
        itemSelected={handleItemSelected}
        selectedItems={selectedTabId}
        selectionVariant="none"
        setDropdownOpen={setOpen}
        size="xs"
      />
    </>
  );
});

const SortablePrimaryTab = memo(function SortablePrimaryTab({
  tab,
  lastDragTimeRef,
  selectedTabId,
  measureRef,
}: {
  tab: LinkPrimaryTab;
  lastDragTimeRef: React.MutableRefObject<number>;
  selectedTabId: string | undefined;
  measureRef: (element: HTMLElement | null) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    // Block pointer events on the inner link while dragging
    pointerEvents: isDragging ? "none" : undefined,
  };

  const handleClickCapture = useCallback(
    (e: React.MouseEvent) => {
      // Block clicks for 500ms after any drag ends
      if (Date.now() - lastDragTimeRef.current < 500) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [lastDragTimeRef],
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClickCapture={handleClickCapture}
    >
      <div ref={measureRef}>
        <PrimaryNavigationTab
          sectionType="active"
          selectedTabId={selectedTabId}
          tab={tab}
        />
      </div>
    </div>
  );
});

const ACTIVE_NAV_ORDER_KEY = "redo_nav_order_active";

function readStoredOrder(): string[] {
  try {
    const raw = localStorage.getItem(ACTIVE_NAV_ORDER_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const SortableActiveSection = memo(function SortableActiveSection({
  tabs,
  selectedTabId,
  onReorder,
  overflowedActiveTabIds = [],
  measureTab,
}: {
  tabs: LinkPrimaryTab[];
  selectedTabId: string | undefined;
  onReorder: (newOrder: string[]) => void;
  overflowedActiveTabIds?: string[];
  measureTab: (id: string) => (element: HTMLElement | null) => void;
}) {
  const lastDragTimeRef = useRef(0);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      lastDragTimeRef.current = Date.now();

      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = tabs.findIndex((t) => t.id === active.id);
      const newIndex = tabs.findIndex((t) => t.id === over.id);
      amplitude.track("nav/tab-reordered", {
        tab: active.id,
        fromIndex: oldIndex,
        toIndex: newIndex,
      });
      const reordered = arrayMove(tabs, oldIndex, newIndex);
      const newOrder = [
        ...reordered.map((t) => t.id),
        ...overflowedActiveTabIds,
      ];
      onReorder(newOrder);
    },
    [tabs, onReorder, overflowedActiveTabIds],
  );

  const tabIds = useMemo(() => tabs.map((t) => t.id), [tabs]);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext items={tabIds} strategy={verticalListSortingStrategy}>
        <Flex dir="column" gap="xl">
          {tabs.map((tab) => (
            <SortablePrimaryTab
              key={tab.id}
              lastDragTimeRef={lastDragTimeRef}
              measureRef={measureTab(tab.id)}
              selectedTabId={selectedTabId}
              tab={tab}
            />
          ))}
        </Flex>
      </SortableContext>
    </DndContext>
  );
});

const PRIMARY_NAV_WIDTH_KEY = "redo_primary_nav_width";
const DEFAULT_PRIMARY_NAV_WIDTH = 72;
const MIN_PRIMARY_NAV_WIDTH = 48;
const ICON_ONLY_THRESHOLD = 64;

function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(PRIMARY_NAV_WIDTH_KEY);
    if (raw) {
      const parsed = Number(raw);
      if (
        !Number.isNaN(parsed) &&
        parsed >= MIN_PRIMARY_NAV_WIDTH &&
        parsed <= DEFAULT_PRIMARY_NAV_WIDTH
      ) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return DEFAULT_PRIMARY_NAV_WIDTH;
}

export const PrimaryNavIconOnlyContext = createContext(false);

export const RedoPrimaryNav = memo(function RedoPrimaryNav({
  toggleCollapseMode,
  isCollapseMode,
  enterSettingsUrl,
  settingsRoot,
  primaryTabSections,
  selectedTabId,
  teamSwitcher,
  actions,
  userProfile,
}: RedoPrimaryNavProps) {
  return (
    <NewPrimaryNavLayout
      actions={actions}
      enterSettingsUrl={enterSettingsUrl}
      isCollapseMode={isCollapseMode}
      primaryTabSections={primaryTabSections}
      selectedTabId={selectedTabId}
      settingsRoot={settingsRoot}
      teamSwitcher={teamSwitcher}
      toggleCollapseMode={toggleCollapseMode}
      userProfile={userProfile}
    />
  );
});

const NewPrimaryNavLayout = memo(function NewPrimaryNavLayout({
  toggleCollapseMode,
  isCollapseMode,
  enterSettingsUrl,
  settingsRoot,
  primaryTabSections,
  selectedTabId,
  teamSwitcher,
  actions,
  userProfile,
}: RedoPrimaryNavProps) {
  const { t } = useTranslation();

  const [navWidth, setNavWidth] = useState(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const isDragging = useRef(false);
  const lastX = useRef(0);

  const iconOnly = navWidth < ICON_ONLY_THRESHOLD;

  const [storedOrder, setStoredOrder] = useState<string[]>(readStoredOrder);

  const handleReorder = useCallback((newOrder: string[]) => {
    setStoredOrder(newOrder);
    localStorage.setItem(ACTIVE_NAV_ORDER_KEY, JSON.stringify(newOrder));
  }, []);

  const orderedActiveProducts = useMemo(
    () => applyStoredOrder(primaryTabSections.activeProducts, storedOrder),
    [primaryTabSections.activeProducts, storedOrder],
  );

  // The Add button is a virtual trailing slot whose dropdown contains the
  // inactive products. It counts as one item for overflow math but is
  // rendered separately (and folded into More if it overflows).
  const flatTabs = useMemo(
    () => [...primaryTabSections.core, ...orderedActiveProducts],
    [primaryTabSections.core, orderedActiveProducts],
  );

  const coreCount = primaryTabSections.core.length;
  const activeCount = primaryTabSections.activeProducts.length;
  const hasInactive = primaryTabSections.inactiveProducts.length > 0;
  const slotIds = useMemo(
    () => [
      ...flatTabs.map((tab) => tab.id),
      ...(hasInactive ? [ADD_SLOT_ID] : []),
    ],
    [flatTabs, hasInactive],
  );

  // Section boundaries: indices where dividers appear between sections.
  // A divider renders before active products (at coreCount) when there are
  // any active products. The Add button replaces the prior inactive-products
  // divider, so no boundary is added for it.
  const sectionBoundaries = useMemo(() => {
    const boundaries: number[] = [];
    if (activeCount > 0) {
      boundaries.push(coreCount);
    }
    return boundaries;
  }, [coreCount, activeCount]);

  const layoutKey = `${navWidth}|${iconOnly}|${flatTabs
    .map((tab) => `${tab.friendlyName}${tab.hideLabel ? "-hidden" : ""}`)
    .join(",")}`;

  const { visibleCount, isOverflowing, containerRef, measureTab, measureMore } =
    usePrimaryNavOverflow(slotIds, sectionBoundaries, layoutKey);

  // Visible counts split across the real tabs and the trailing Add slot.
  const visibleRealCount = Math.min(visibleCount, flatTabs.length);
  const showAddButton = hasInactive && visibleCount > flatTabs.length;
  const overflowTabs = flatTabs.slice(visibleRealCount);

  const hasVisibleActive = visibleRealCount > coreCount && activeCount > 0;

  const visibleCoreTabs = flatTabs.slice(
    0,
    Math.min(visibleRealCount, coreCount),
  );
  const visibleActiveTabs = flatTabs.slice(coreCount, visibleRealCount);
  const overflowedActiveTabIds = useMemo(
    () =>
      orderedActiveProducts.slice(visibleActiveTabs.length).map((t) => t.id),
    [orderedActiveProducts, visibleActiveTabs.length],
  );

  const handleMouseDown = useHandler((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    lastX.current = e.clientX;
    setIsResizing(true);
  });

  useHtmlEventListener(document.documentElement, "mousemove", (e) => {
    if (!isDragging.current) {
      return;
    }
    const delta = e.clientX - lastX.current;
    lastX.current = e.clientX;
    setNavWidth((prev) =>
      Math.max(
        MIN_PRIMARY_NAV_WIDTH,
        Math.min(DEFAULT_PRIMARY_NAV_WIDTH, prev + delta),
      ),
    );
  });

  useHtmlEventListener(document.documentElement, "mouseup", () => {
    if (!isDragging.current) {
      return;
    }
    isDragging.current = false;
    setIsResizing(false);
    setNavWidth((w) => {
      localStorage.setItem(PRIMARY_NAV_WIDTH_KEY, String(w));
      return w;
    });
  });

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--redo-primary-nav-width",
      `${navWidth}px`,
    );
    return () => {
      document.documentElement.style.removeProperty("--redo-primary-nav-width");
    };
  }, [navWidth]);

  return (
    <PrimaryNavIconOnlyContext.Provider value={iconOnly}>
      <Flex
        align="center"
        bgColor="tertiary"
        className={classNames(
          darkModeCss.darkMode,
          navigationCss.primaryNav,
          isResizing && navigationCss.primaryNavResizing,
        )}
        data-arbiter="RedoPrimaryNav"
        data-testid="primary-nav-root"
        dir="column"
        grow={1}
        justify="space-between"
        px="xs"
        py="xl"
        style={{ width: navWidth, maxWidth: navWidth }}
      >
        <Flex
          align="center"
          className={navigationCss.primaryNavTabSection}
          dir="column"
          gap="xl"
          grow={1}
          shrink={1}
        >
          {teamSwitcher}
          <Flex
            className={navigationCss.primaryNavTabSection}
            data-testid="primary-tab-column"
            dir="column"
            gap="xl"
            grow={1}
            ref={containerRef}
            shrink={1}
          >
            {visibleCoreTabs.map((tab) => (
              <div key={tab.id} ref={measureTab(tab.id)}>
                <PrimaryNavigationTab
                  sectionType="core"
                  selectedTabId={selectedTabId}
                  tab={tab}
                />
              </div>
            ))}
            {hasVisibleActive && (
              <>
                <Flex className={navigationCss.primaryNavDivider} />
                <SortableActiveSection
                  measureTab={measureTab}
                  onReorder={handleReorder}
                  overflowedActiveTabIds={overflowedActiveTabIds}
                  selectedTabId={selectedTabId}
                  tabs={visibleActiveTabs}
                />
              </>
            )}
            {showAddButton && (
              <PrimaryNavAddPopover
                availableTabs={primaryTabSections.inactiveProducts}
                measureRef={measureTab(ADD_SLOT_ID)}
                selectedTabId={selectedTabId}
              />
            )}
            {isOverflowing && (
              <PrimaryNavOverflowPopover
                addSectionTabs={
                  hasInactive && !showAddButton
                    ? primaryTabSections.inactiveProducts
                    : []
                }
                measureRef={measureMore}
                overflowTabs={overflowTabs}
                selectedTabId={selectedTabId}
              />
            )}
          </Flex>
        </Flex>
        <Flex align="center" dir="column" gap="xl" shrink={0}>
          <PrimaryNavigationTab
            manuallySelected={isCollapseMode}
            tab={{
              id: "collapse",
              UnfilledIcon: LayoutLeft,
              FilledIcon: LayoutLeftFilled,
              type: "button",
              friendlyName: t("Collapse", { context: "navigation menu" }),
              hasNotification: false,
              hideLabel: true,
              onClick: toggleCollapseMode,
            }}
          />
          {actions}
          {enterSettingsUrl && (
            <PrimaryNavigationTab
              tab={{
                id: "settings",
                hasNotification: false,
                UnfilledIcon: GearIcon,
                FilledIcon: GearFilledIcon,
                type: "link",
                href: enterSettingsUrl,
                hideLabel: true,
                friendlyName: t("Settings", { context: "navigation menu" }),
                pathRoot: settingsRoot,
              }}
            />
          )}
          {userProfile}
        </Flex>
        <div
          className={navigationCss.primaryNavResizeHandle}
          data-resizing={isResizing || undefined}
          data-testid="primary-nav-resize-handle"
          onMouseDown={handleMouseDown}
        />
      </Flex>
    </PrimaryNavIconOnlyContext.Provider>
  );
});
